import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Grid,
  Alert,
  Snackbar,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy,
  PeopleAlt,
  MonetizationOn,
  TouchApp,
  HowToReg,
} from '@mui/icons-material';
import apiClient from '../api/apiClient';

interface AffiliateStats {
  clicks: number;
  signups: number;
  conversions: number;
  total_earned: number;
}

interface AffiliateProfile {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  payout_email: string | null;
  commission_rate: number;
  status: string;
  created_at: string;
  stats: AffiliateStats;
}

interface Referral {
  id: string;
  referred_email: string | null;
  referred_user_id: string | null;
  status: string;
  product: string | null;
  revenue_amount: number | null;
  commission_amount: number | null;
  converted_at: string | null;
  created_at: string;
}

const statusColor = (
  s: string,
): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  switch (s) {
    case 'converted':
      return 'success';
    case 'signed_up':
      return 'primary';
    case 'clicked':
      return 'warning';
    default:
      return 'default';
  }
};

const StatCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            bgcolor: `${color}22`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            color,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ReferralPage: React.FC = () => {
  const [affiliate, setAffiliate] = useState<AffiliateProfile | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Registration form state
  const [regName, setRegName] = useState('');
  const [regPayoutEmail, setRegPayoutEmail] = useState('');

  const appUrl =
    process.env.REACT_APP_APP_URL ||
    `${window.location.protocol}//${window.location.host}`;

  const referralLink = affiliate
    ? `${appUrl}/?ref=${affiliate.referral_code}`
    : '';

  const fetchAffiliate = async () => {
    try {
      const res = await apiClient.get('/affiliates/me');
      setAffiliate(res.data);
    } catch (err: any) {
      if (err.response?.status !== 404) {
        setError('Failed to load affiliate profile.');
      }
      // 404 means not registered — that's fine
    }
  };

  const fetchReferrals = async () => {
    try {
      const res = await apiClient.get('/affiliates/referrals');
      setReferrals(res.data);
    } catch {
      // Silently ignore if not yet an affiliate
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchAffiliate();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (affiliate) {
      fetchReferrals();
    }
  }, [affiliate]);

  const handleRegister = async () => {
    if (!regName.trim()) {
      setError('Please enter your name.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/affiliates/register', {
        name: regName.trim(),
        payout_email: regPayoutEmail.trim() || undefined,
      });
      setSuccess('Welcome to the affiliate program!');
      await fetchAffiliate();
    } catch (err: any) {
      const msg =
        err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setSuccess('Referral link copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Referral Program
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Refer new users to ASCIN and earn 20% commission on their subscriptions.
      </Typography>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Not yet an affiliate — show registration form                       */}
      {/* ------------------------------------------------------------------ */}
      {!affiliate && (
        <Card sx={{ maxWidth: 520 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Join the Affiliate Program
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Fill in the details below to receive your unique referral link and
              start earning commissions.
            </Typography>

            <TextField
              label="Your Name"
              fullWidth
              value={regName}
              onChange={(e) => setRegName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              label="Payout Email (optional)"
              fullWidth
              type="email"
              value={regPayoutEmail}
              onChange={(e) => setRegPayoutEmail(e.target.value)}
              sx={{ mb: 3 }}
              helperText="Where we'll send your commission payouts"
            />
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              onClick={handleRegister}
              startIcon={submitting ? <CircularProgress size={18} /> : <PeopleAlt />}
            >
              {submitting ? 'Joining…' : 'Join Affiliate Program'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Registered affiliate dashboard                                      */}
      {/* ------------------------------------------------------------------ */}
      {affiliate && (
        <>
          {/* Referral link */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Your Referral Link
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  value={referralLink}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
                <Tooltip title="Copy link">
                  <IconButton onClick={handleCopyLink} color="primary">
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  onClick={handleCopyLink}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Copy Link
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                Code: <strong>{affiliate.referral_code}</strong> &nbsp;|&nbsp; Commission:{' '}
                <strong>{affiliate.commission_rate}%</strong> &nbsp;|&nbsp; Status:{' '}
                <Chip label={affiliate.status} size="small" color="success" />
              </Typography>
            </CardContent>
          </Card>

          {/* Stat cards */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Clicks"
                value={affiliate.stats.clicks}
                icon={<TouchApp />}
                color="#1976d2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Sign-ups"
                value={affiliate.stats.signups}
                icon={<HowToReg />}
                color="#7b1fa2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Conversions"
                value={affiliate.stats.conversions}
                icon={<PeopleAlt />}
                color="#00796b"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total Earned"
                value={`$${affiliate.stats.total_earned.toFixed(2)}`}
                icon={<MonetizationOn />}
                color="#f57c00"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Referrals table */}
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Your Referrals
          </Typography>
          {referrals.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No referrals yet. Share your link to get started!
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Referred Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referrals.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        {r.referred_email || (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Unknown
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size="small"
                          color={statusColor(r.status)}
                        />
                      </TableCell>
                      <TableCell>{r.product || '—'}</TableCell>
                      <TableCell align="right">
                        {r.commission_amount != null
                          ? `$${r.commission_amount.toFixed(2)}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {new Date(r.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default ReferralPage;
